<template>
  <div class="rb-ob-form rb-match-page rb-book-page">
    <header><h1 class="rb-ob-title">{{ isFa ? 'تغییر زمان جلسه' : 'Reschedule session' }}</h1></header>
    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="loading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</p>

    <ul v-if="booking || careSession" class="rb-book-summary" aria-label="Session reschedule summary">
      <li v-if="careSession"><span class="rb-book-summary-k">{{ isFa ? 'شماره جلسه' : 'Session number' }}</span><span class="rb-book-summary-v"><bdi>{{ sessionNumberLabel }}</bdi></span></li>
      <li v-else><span class="rb-book-summary-k">{{ isFa ? 'کد رزرو' : 'Booking' }}</span><span class="rb-book-summary-v"><bdi>{{ booking.booking_code || booking.booking_id }}</bdi></span></li>
      <li><span class="rb-book-summary-k">{{ isFa ? 'وضعیت جلسه' : 'Session status' }}</span><span class="rb-book-summary-v">{{ bookingStatusLabel }}</span></li>
      <li>
        <span class="rb-book-summary-k">{{ isFa ? 'امکان تغییر زمان' : 'Can change time' }}</span>
        <span class="rb-book-summary-v">{{ changeAvailabilityLabel }}</span>
      </li>
      <li v-if="deadlineLabel && deadlineLabel !== '—'">
        <span class="rb-book-summary-k">{{ isFa ? 'مهلت تغییر' : 'Change deadline' }}</span>
        <span class="rb-book-summary-v"><bdi>{{ deadlineLabel }}</bdi></span>
      </li>
      <li v-if="changesRemainingLabel !== '—'">
        <span class="rb-book-summary-k">{{ isFa ? 'تغییرهای باقی‌مانده' : 'Changes remaining' }}</span>
        <span class="rb-book-summary-v"><bdi>{{ changesRemainingLabel }}</bdi></span>
      </li>
    </ul>

    <div v-if="!loading && !needsLogin && actionAllowed" class="rb-reschedule-allow">
      <p class="rb-match-outcome-body rb-reschedule-allow-copy">{{ allowBody }}</p>

      <div v-if="slotsLoading" class="rb-ob-status" role="status">{{ $t('intake.portal.loading') }}</div>

      <div v-else-if="slotDays.length" class="rb-booking-fields rb-reschedule-slots">
        <div class="rb-ob-field-block">
          <p class="rb-ob-q">{{ isFa ? 'روز جایگزین' : 'Pick a day' }}</p>
          <div class="rb-date-grid" role="listbox" :aria-label="isFa ? 'روزهای جایگزین' : 'Alternative days'">
            <button
              v-for="day in slotDays"
              :key="day.key"
              type="button"
              class="rb-date-card rb-reschedule-day"
              :class="{ 'is-selected': selectedDayKey === day.key }"
              :aria-pressed="selectedDayKey === day.key ? 'true' : 'false'"
              @click="selectDay(day.key)"
            >
              <span class="rb-reschedule-day-dow">{{ day.dow }}</span>
              <span class="rb-reschedule-day-date"><bdi>{{ day.dateLabel }}</bdi></span>
            </button>
          </div>
        </div>

        <div class="rb-ob-field-block">
          <p class="rb-ob-q">{{ isFa ? 'ساعت جایگزین' : 'Pick a time' }}</p>
          <div class="rb-time-grid" role="listbox" :aria-label="isFa ? 'ساعت‌های جایگزین' : 'Alternative times'">
            <label
              v-for="slot in slotsForSelectedDay"
              :key="slot.id"
              class="rb-time-card"
              :class="{ 'is-selected': selectedSlotId === String(slot.id) }"
            >
              <input
                v-model="selectedSlotId"
                type="radio"
                name="reschedule-slot"
                :value="String(slot.id)"
              >
              <span><bdi>{{ formatTime(slot) }}</bdi></span>
            </label>
          </div>
          <p v-if="selectedSlotId" class="rb-ob-timezone-note">
            {{ isFa ? 'زمان انتخاب‌شده: ' : 'Selected: ' }}
            <bdi>{{ selectedSlotSummary }}</bdi>
          </p>
        </div>
      </div>

      <p v-else class="rb-book-slot-empty is-visible" role="status">
        {{ isFa ? 'الان زمان خالی نیست. بعداً دوباره امتحان کن.' : 'No open times right now. Try again later.' }}
      </p>
    </div>

    <div v-else-if="!loading && !needsLogin" class="rb-ob-status-panel">
      <p class="rb-match-outcome-body">{{ blockedBody }}</p>
    </div>

    <div class="rb-ob-actions">
      <button
        v-if="actionAllowed"
        class="rb-ob-submit"
        type="button"
        :disabled="loading || committing || !selectedSlotId || needsLogin"
        @click="commit"
      >
        {{ isFa ? 'ثبت زمان جدید' : 'Save new time' }}
      </button>
      <button class="rb-ob-submit" type="button" :disabled="loading || needsLogin" @click="load">
        {{ isFa ? 'به‌روزرسانی وضعیت' : 'Refresh status' }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="back">
        {{ isFa ? 'بازگشت' : 'Back' }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ error || success }}</p>
    </div>
  </div>
</template>

<script>
import {
  commitBookingReschedule,
  fetchBookingOperations,
  fetchBookingReschedulePreview,
  fetchCareSessionReschedulePreview,
  rescheduleCareSession,
  fetchRescheduleDoctorSlots,
  formatRescheduleDeadline,
  formatRescheduleSlotDayKey,
  formatRescheduleSlotDayLabel,
  formatRescheduleSlotLabel,
  formatRescheduleSlotTimeLabel,
  hasAuthToken,
  toFaDigits,
} from '@/intake/portalApi'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export default {
  name: 'SessionRescheduleStep',
  data() {
    return {
      loading: false,
      slotsLoading: false,
      committing: false,
      needsLogin: false,
      snapshot: null,
      preview: null,
      slots: [],
      selectedDayKey: '',
      selectedSlotId: '',
      error: '',
      success: '',
    }
  },
  computed: {
    lang() { return this.$route.params.lang || 'fa' },
    isFa() { return this.lang === 'fa' },
    sessionId() {
      const hint = this.routeBookingHint()
      return hint && /^\d+$/.test(String(hint)) ? String(hint) : null
    },
    careSession() { return (this.preview && this.preview.session) || null },
    sessionNumberLabel() {
      const number = this.careSession && this.careSession.session_number
      return Number(number) > 0 ? (this.isFa ? toFaDigits(number) : String(number)) : '—'
    },
    booking() {
      return (this.preview && this.preview.booking)
        || (this.snapshot && this.snapshot.booking)
        || null
    },
    actionAllowed() {
      return !!(this.preview && this.preview.action_allowed === true && this.preview.action === 'RESCHEDULE')
    },
    reasonCode() {
      return (this.preview && this.preview.reason_code)
        || (!this.booking && !this.careSession && !this.loading ? 'NO_SERVER_BOOKING' : '')
    },
    bookingStatusLabel() {
      const raw = (this.careSession && this.careSession.state) || (this.booking && (this.booking.client_status || this.booking.state)) || ''
      const mapFa = {
        CONFIRMED: 'تأییدشده',
        AWAITING_PAYMENT: 'در انتظار پرداخت',
        PROCESSING: 'در حال پردازش',
        PAYMENT_LATE: 'نیاز به پیگیری مالی',
        PAYMENT_FAILED: 'پرداخت ناموفق',
        CANCELLED: 'لغوشده',
        date_fixed: 'تأییدشده',
        canceled: 'لغوشده',
        used: 'برگزارشده',
      }
      const mapEn = {
        CONFIRMED: 'Confirmed',
        AWAITING_PAYMENT: 'Awaiting payment',
        PROCESSING: 'Processing',
        PAYMENT_LATE: 'Needs finance follow-up',
        PAYMENT_FAILED: 'Payment failed',
        CANCELLED: 'Cancelled',
        date_fixed: 'Confirmed',
        canceled: 'Cancelled',
        used: 'Completed',
      }
      const map = this.isFa ? mapFa : mapEn
      return map[raw] || raw || '—'
    },
    changeAvailabilityLabel() {
      if (this.actionAllowed) return this.isFa ? 'بله' : 'Yes'
      if (!this.preview && this.loading) return '—'
      return this.isFa ? 'خیر' : 'No'
    },
    deadlineLabel() {
      if (!(this.preview && this.preview.deadline)) return '—'
      return formatRescheduleDeadline(this.preview.deadline, this.lang)
    },
    changesRemainingLabel() {
      const n = this.preview && this.preview.changes_remaining
      if (n === null || n === undefined) return '—'
      return this.isFa ? toFaDigits(n) : String(n)
    },
    allowBody() {
      return this.isFa
        ? 'می‌توانی زمان جلسه را تا مهلت اعلام‌شده و در سقف تغییرهای باقی‌مانده عوض کنی. هزینهٔ اضافه تعریف نشده است.'
        : 'You can change the session time before the deadline and within the remaining change limit. No extra fee applies.'
    },
    blockedBody() {
      return this.humanReason(this.reasonCode)
    },
    slotDays() {
      const byKey = new Map()
      for (const slot of this.slots) {
        const key = formatRescheduleSlotDayKey(slot, this.lang)
        if (!key || byKey.has(key)) continue
        const full = formatRescheduleSlotDayLabel(slot, this.lang)
        const parts = String(full).split(/\s+/)
        byKey.set(key, {
          key,
          dow: parts[0] || full,
          dateLabel: parts.slice(1).join(' ') || full,
          full,
        })
      }
      return Array.from(byKey.values())
    },
    slotsForSelectedDay() {
      if (!this.selectedDayKey) return []
      return this.slots.filter((slot) => formatRescheduleSlotDayKey(slot, this.lang) === this.selectedDayKey)
    },
    selectedSlotSummary() {
      const slot = this.slots.find((s) => String(s.id) === String(this.selectedSlotId))
      return slot ? formatRescheduleSlotLabel(slot, this.lang) : ''
    },
  },
  mounted() { this.load() },
  methods: {
    humanReason(code) {
      const mapFa = {
        RESCHEDULE_TOO_LATE: 'مهلت تغییر زمان این جلسه گذشته است. برای کمک با پشتیبانی تماس بگیر.',
        RESCHEDULE_MAX_CHANGES: 'سقف تعداد تغییر زمان برای این رزرو پر شده است.',
        BOOKING_STATE_NOT_RESCHEDULABLE: 'وضعیت فعلی این رزرو اجازهٔ تغییر زمان نمی‌دهد.',
        RESCHEDULE_SESSION_TIME_UNKNOWN: 'زمان جلسه از سرور قابل خواندن نیست. لطفاً بعداً دوباره امتحان کن یا با پشتیبانی تماس بگیر.',
        RESCHEDULE_TEMPLATE_MISSING: 'الان امکان تغییر زمان این جلسه وجود ندارد. لطفاً بعداً دوباره امتحان کن.',
        RESCHEDULE_FEE_UNSUPPORTED: 'تغییر زمان با هزینهٔ اضافه هنوز در دسترس نیست.',
        RESCHEDULE_SAME_SLOT: 'این همان زمان فعلی جلسه است. زمان دیگری را انتخاب کن.',
        RESCHEDULE_SESSION_MISSING: 'جلسهٔ مرتبط با این رزرو پیدا نشد.',
        SLOT_NOT_AVAILABLE: 'این زمان دیگر خالی نیست. زمان دیگری را انتخاب کن.',
        SLOT_NOT_FOUND: 'زمان انتخاب‌شده پیدا نشد.',
        SLOT_DOCTOR_MISMATCH: 'این زمان متعلق به مشاور این رزرو نیست.',
        NO_SERVER_BOOKING: 'رزرو تأییدشده‌ای برای تغییر زمان پیدا نشد.',
        RESCHEDULE_COMMIT_FAILED: 'ثبت زمان جدید ممکن نشد. لطفاً دوباره امتحان کن.',
        RESCHEDULE_STATUS_FAILED: 'خواندن وضعیت تغییر زمان ممکن نشد.',
        SESSION_STATE_NOT_RESCHEDULABLE: 'وضعیت فعلی این جلسه اجازهٔ تغییر زمان نمی‌دهد.',
        SESSION_NOT_OWNED: 'شما دسترسی به تغییر زمان این جلسه را ندارید.',
        SESSION_NOT_FOUND: 'این جلسه برای تغییر زمان پیدا نشد.',
        RESCHEDULE_SLOT_IN_PAST: 'زمان انتخاب‌شده گذشته است. زمان دیگری را انتخاب کن.',
      }
      const mapEn = {
        RESCHEDULE_TOO_LATE: 'The reschedule deadline for this session has passed. Contact support if you need help.',
        RESCHEDULE_MAX_CHANGES: 'This booking has used all allowed reschedule changes.',
        BOOKING_STATE_NOT_RESCHEDULABLE: 'The current booking state cannot be rescheduled.',
        RESCHEDULE_SESSION_TIME_UNKNOWN: 'The session time could not be read from the server. Try again later or contact support.',
        RESCHEDULE_TEMPLATE_MISSING: 'Rescheduling is unavailable right now. Please try again later.',
        RESCHEDULE_FEE_UNSUPPORTED: 'Paid reschedule is not available yet.',
        RESCHEDULE_SAME_SLOT: 'That is already the current session time. Pick a different slot.',
        RESCHEDULE_SESSION_MISSING: 'The session linked to this booking was not found.',
        SLOT_NOT_AVAILABLE: 'That time is no longer open. Pick another slot.',
        SLOT_NOT_FOUND: 'The selected time was not found.',
        SLOT_DOCTOR_MISMATCH: 'That time does not belong to this booking’s therapist.',
        NO_SERVER_BOOKING: 'No confirmed booking was found to reschedule.',
        RESCHEDULE_COMMIT_FAILED: 'Could not save the new time. Please try again.',
        RESCHEDULE_STATUS_FAILED: 'Could not load reschedule status.',
        SESSION_STATE_NOT_RESCHEDULABLE: 'The current session state cannot be rescheduled.',
        SESSION_NOT_OWNED: 'You cannot reschedule this session.',
        SESSION_NOT_FOUND: 'This session was not found for rescheduling.',
        RESCHEDULE_SLOT_IN_PAST: 'That time is in the past. Pick another slot.',
      }
      const map = this.isFa ? mapFa : mapEn
      if (code && map[code]) return map[code]
      return this.isFa
        ? 'الان امکان تغییر زمان این جلسه وجود ندارد. در صورت نیاز با پشتیبانی تماس بگیر.'
        : 'This session cannot be rescheduled right now. Contact support if you need help.'
    },
    routeBookingHint() {
      const q = this.$route.query || {}
      return q.booking_id || q.bookingId || q.id || null
    },
    resolveBookingId(snapshot) {
      const hint = this.routeBookingHint()
      if (hint && UUID_RE.test(String(hint))) return String(hint)
      if (snapshot && snapshot.booking && snapshot.booking.booking_id) return snapshot.booking.booking_id
      return null
    },
    formatTime(slot) {
      return formatRescheduleSlotTimeLabel(slot, this.lang)
    },
    selectDay(key) {
      this.selectedDayKey = key
      const stillVisible = this.slotsForSelectedDay.some((s) => String(s.id) === String(this.selectedSlotId))
      if (!stillVisible) this.selectedSlotId = ''
    },
    syncDaySelection() {
      const days = this.slotDays
      if (!days.length) {
        this.selectedDayKey = ''
        return
      }
      if (!days.some((d) => d.key === this.selectedDayKey)) {
        this.selectedDayKey = days[0].key
      }
    },
    loadSlots(doctorId) {
      if (!doctorId) {
        this.slots = []
        this.selectedDayKey = ''
        return Promise.resolve()
      }
      this.slotsLoading = true
      return fetchRescheduleDoctorSlots(doctorId)
        .then((times) => {
          const target = this.careSession || this.booking
          const currentSlot = target && target.slot_id ? String(target.slot_id) : ''
          this.slots = (times || []).filter((t) => String(t.id) !== currentSlot)
          this.syncDaySelection()
        })
        .catch(() => {
          this.slots = []
          this.selectedDayKey = ''
          this.error = this.isFa ? 'بارگذاری زمان‌های جایگزین ممکن نشد.' : 'Could not load alternative times.'
        })
        .finally(() => { this.slotsLoading = false })
    },
    load() {
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) return Promise.resolve()
      this.loading = true
      this.error = ''
      this.success = ''
      this.snapshot = null
      this.preview = null
      this.slots = []
      this.selectedDayKey = ''
      this.selectedSlotId = ''
      if (this.sessionId) {
        return fetchCareSessionReschedulePreview(this.sessionId).then((preview) => {
          this.preview = preview
          if (this.actionAllowed) return this.loadSlots(preview.doctor_id || (preview.session && preview.session.doctor_id))
          return null
        }).catch((e) => {
          if (e && e.response && e.response.status === 401) this.needsLogin = true
          const code = (e && e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code)
            || (e && e.response && e.response.status === 404 ? 'SESSION_NOT_FOUND' : 'RESCHEDULE_STATUS_FAILED')
          this.error = this.humanReason(code)
        }).finally(() => { this.loading = false })
      }
      const hint = this.routeBookingHint()
      const sessionOpts = (hint && /^\d+$/.test(String(hint))) ? {userSessionId: hint} : {}
      return fetchBookingOperations(sessionOpts).then((snapshot) => {
        this.snapshot = snapshot || null
        const bookingId = this.resolveBookingId(snapshot)
        if (!bookingId) return null
        return fetchBookingReschedulePreview(bookingId).then((preview) => {
          this.preview = preview
          if (preview && preview.action_allowed === true) {
            const doctorId = (preview.doctor_id != null)
              ? preview.doctor_id
              : (preview.booking && preview.booking.doctor_id)
            return this.loadSlots(doctorId)
          }
          return null
        })
      }).catch((e) => {
        this.error = this.humanReason((e && e.message) || 'RESCHEDULE_STATUS_FAILED')
      }).finally(() => { this.loading = false })
    },
    commit() {
      if (!this.actionAllowed || !this.selectedSlotId || (!this.booking && !this.careSession)) return Promise.resolve()
      this.committing = true
      this.error = ''
      this.success = ''
      const request = this.sessionId
        ? rescheduleCareSession(this.sessionId, this.selectedSlotId)
        : commitBookingReschedule(this.booking.booking_id, this.selectedSlotId)
      return request
        .then((result) => {
          this.preview = result
          this.success = this.isFa ? 'زمان جدید ذخیره شد.' : 'New time saved.'
          this.selectedSlotId = ''
          const doctorId = (result && result.doctor_id != null)
            ? result.doctor_id
            : (result && ((result.session && result.session.doctor_id) || (result.booking && result.booking.doctor_id)))
          return this.loadSlots(doctorId)
        })
        .catch((e) => {
          const code = (e && e.response && e.response.data && e.response.data.data && e.response.data.data.reason_code)
            || (e && e.response && e.response.data && e.response.data.message)
            || (e && e.message)
            || 'RESCHEDULE_COMMIT_FAILED'
          this.error = this.humanReason(String(code))
        })
        .finally(() => { this.committing = false })
    },
    back() {
      const q = this.$route.query || {}
      if (q.id) {
        this.$router.push({name: 'portalSessionDetail', params: {lang: this.lang}, query: {id: String(q.id)}})
        return
      }
      this.$router.push({name: 'portalHome', params: {lang: this.lang}})
    },
  },
}
</script>

<style scoped>
.rb-reschedule-allow {
  margin: 0 0 1rem;
}

.rb-reschedule-allow-copy {
  margin: 0 0 1rem;
  padding: 0.9rem 1rem;
  border-radius: var(--r-md, 0.875rem);
  background: color-mix(in srgb, var(--rb-sand, #e8dfd0) 55%, var(--rb-white, #fff));
  color: var(--s-ink-secondary, #4a6675);
  line-height: 1.75;
}

.rb-reschedule-slots {
  display: grid;
  gap: 1rem;
}

.rb-reschedule-day {
  appearance: none;
  font: inherit;
  flex-direction: column;
  gap: 0.15rem;
  min-height: 3.6rem;
  padding: 0.7rem 0.55rem;
  border: 1.5px solid var(--s-line-hairline, var(--rb-sand));
  border-radius: var(--r-md, 0.875rem);
  background: var(--s-surface-card, var(--rb-white));
  color: var(--s-ink-primary, var(--rb-navy));
  cursor: pointer;
  text-align: center;
}

.rb-reschedule-day.is-selected {
  border-color: var(--rb-teal);
  background: var(--rb-teal-soft, #dff5f1);
  box-shadow: 0 0 0 1px rgb(22 164 155 / 0.2);
}

.rb-reschedule-day-dow {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--s-ink-muted, #5a6e68);
}

.rb-reschedule-day.is-selected .rb-reschedule-day-dow {
  color: var(--rb-teal);
}

.rb-reschedule-day-date {
  display: block;
  font-size: 0.92rem;
  font-weight: 700;
}
</style>
